import React, { useMemo, useState } from 'react';
import { ResponsiveContainer, ScatterChart, Scatter, XAxis, YAxis, ZAxis, CartesianGrid, Tooltip, Legend, ReferenceArea } from 'recharts';

// Small helper to safely number
const num = (v, def = null) => {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : def;
};

const calculateDTE = (dateString) => {
  if (!dateString) return null;
  const today = new Date();
  const expirationDate = new Date(dateString);
  today.setHours(0, 0, 0, 0);
  expirationDate.setHours(0, 0, 0, 0);
  const diffTime = expirationDate - today;
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

const buildDataset = (rows) => {
  const points = [];
  rows.forEach((row, idx) => {
    const dte = calculateDTE(row?.calendar_spread?.legs?.[0]?.expiration);
    const rr = num(row?.rr_ratio);
    const vodRaw = num(row?.min_pl_death_valley);
    // Displayed VoD already scaled *100 in table; keep signed to favor "magas" (pozitív) értéket
    const vodDisp = typeof vodRaw === 'number' ? vodRaw * 100 : null;
    const cost = num(row?.total_debit);
    const costUsd = typeof cost === 'number' ? cost * 100 : null;
    const netCostRaw = num(row?.costs?.net_strategy_cost_after_vertical_credit);
    let hedgeDisp = null;
    if (typeof netCostRaw === 'number') {
      hedgeDisp = (netCostRaw < 0 ? Math.abs(netCostRaw) : -netCostRaw) * 100; // same logic as table display
    }
    const netDelta = num(row?.net_delta);
    const shortDelta = (() => {
      const calShort = row?.calendar_spread?.legs?.[0]?.delta;
      if (typeof calShort === 'number') return Math.round(Math.abs(calShort) * 100);
      return null;
    })();
    if (rr !== null && vodDisp !== null) {
      points.push({
        id: row?.id ?? idx,
        rr,
        vodDisp,
        dte,
        costUsd,
        hedgeDisp,
        netDelta,
        shortDelta,
        _row: row,
      });
    }
  });
  return points;
};

const OverviewPanel = ({ data, onSelectCombination }) => {
  const points = useMemo(() => buildDataset(Array.isArray(data) ? data : []), [data]);

  // Unique color per distinct DTE value
  const dteColorMap = useMemo(() => {
    const values = Array.from(new Set(points.map(p => p.dte).filter(v => v != null))).sort((a,b)=>a-b);
    const palette = ['#22c55e','#3b82f6','#a855f7','#f59e0b','#ef4444','#10b981','#8b5cf6','#06b6d4','#f97316','#84cc16','#e879f9','#34d399','#60a5fa'];
    const map = new Map();
    values.forEach((v, i) => { map.set(v, palette[i % palette.length]); });
    return map;
  }, [points]);

  const colorForDte = (dte) => {
    if (dte == null) return '#9ca3af';
    return dteColorMap.get(dte) || '#9ca3af';
  };

  // Weight sliders and ranking
  const [wRR, setWRR] = useState(50);
  const [wHedge, setWHedge] = useState(30);
  const [wVoD, setWVoD] = useState(20);

  const normBounds = (arr, key) => {
    const vals = arr.map(p => p[key]).filter(v => v != null);
    if (!vals.length) return { min: 0, max: 1 };
    let min = Math.min(...vals), max = Math.max(...vals);
    if (min === max) max = min + 1;
    return { min, max };
  };

  const ranked = useMemo(() => {
    if (!points.length) return [];
    const bRR = normBounds(points, 'rr');
    const bH = normBounds(points, 'hedgeDisp');
    const bV = normBounds(points, 'vodDisp');
    const wr = wRR / 100, wh = wHedge / 100, wv = wVoD / 100;
    const score = (p) => (
      wr * ((p.rr - bRR.min) / (bRR.max - bRR.min)) +
      wh * (((p.hedgeDisp ?? bH.min) - bH.min) / (bH.max - bH.min)) +
      wv * (((p.vodDisp ?? bV.min) - bV.min) / (bV.max - bV.min))
    );
    const arr = points.map(p => ({ ...p, _score: score(p) }));
    arr.sort((a,b) => b._score - a._score);
    return arr.slice(0, 12);
  }, [points, wRR, wHedge, wVoD]);

  const presentDteValues = useMemo(() => {
    const s = new Set();
    points.forEach(p => { if (p?.dte != null) s.add(Number(p.dte)); });
    return Array.from(s).sort((a,b)=>a-b);
  }, [points]);

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
      {/* (removed) ranking and top list - now handled in toolbar and table sort */}

      {/* Scatter: R/R vs |VoD| */}
      <div style={{ border: 'none', borderRadius: 12, background: 'var(--surface)', height: 260 }}>
        <div style={{ padding: '6px 10px', fontSize: 12, color: 'var(--muted)' }}>R/R vs VoD (bubble=Hedge, color=DTE)</div>
        <ResponsiveContainer width="100%" height="100%">
          <ScatterChart margin={{ top: 10, right: 20, bottom: 20, left: 30 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
            <XAxis type="number" dataKey="vodDisp" name="VoD ($)" tickFormatter={(v) => Math.round(v)} />
            <YAxis type="number" dataKey="rr" name="R/R (%)" tickFormatter={(v) => Math.round(v)} />
            <ZAxis type="number" dataKey="hedgeDisp" range={[60, 300]} />
            <Tooltip
              cursor={{ strokeDasharray: '3 3' }}
              formatter={(val, name, ctx) => {
                const n = name === 'hedgeDisp' ? 'Hedge' : name;
                return [Math.round(val), n];
              }}
              labelFormatter={() => ''}
              content={(props) => {
                if (!props || !props.active || !props.payload || !props.payload.length) return null;
                const p = props.payload[0].payload;
                return (
                  <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', padding: 8, borderRadius: 8, fontSize: 12 }}>
                    <div>DTE: {p?.dte ?? '—'}</div>
                    <div>R/R: {Math.round(p?.rr ?? 0)}</div>
                    <div>VoD: {Math.round(p?.vodDisp ?? 0)}</div>
                    <div>Hedge: {Math.round(p?.hedgeDisp ?? 0)}</div>
                    <div>Cost: {Math.round((p?.costUsd ?? 0))}</div>
                  </div>
                );
              }}
            />
            <Legend payload={presentDteValues.map(d => ({ value: `${d} DTE`, type: 'square', color: colorForDte(d) }))} />
            <Scatter name="Combos" data={points} onClick={(e)=> onSelectCombination?.(e?.payload?._row)} shape={(props) => {
              const { cx, cy, size, payload } = props;
              const r = Math.sqrt(size) / 2.5;
              const fill = colorForDte(payload.dte);
              return <circle cx={cx} cy={cy} r={Math.max(3, r)} fill={fill} stroke={fill} strokeWidth={0.5} />;
            }} />
          </ScatterChart>
        </ResponsiveContainer>
      </div>
      <div style={{ border: 'none', borderRadius: 12, background: 'var(--surface)', height: 260 }}>
        <div style={{ padding: '6px 10px', fontSize: 12, color: 'var(--muted)' }}>R/R vs Net Δ (bubble=Hedge, color=DTE)</div>
        <ResponsiveContainer width="100%" height="100%">
          <ScatterChart margin={{ top: 10, right: 20, bottom: 20, left: 30 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
            <XAxis type="number" dataKey="netDelta" name="Net Δ" tickFormatter={(v) => Number(v).toFixed(2)} />
            <YAxis type="number" dataKey="rr" name="R/R (%)" tickFormatter={(v) => Math.round(v)} />
            <ZAxis type="number" dataKey="hedgeDisp" range={[60, 300]} />
            <Tooltip cursor={{ strokeDasharray: '3 3' }} formatter={(val, name) => [Math.round(val), name]} labelFormatter={() => ''} content={(props) => {
              if (!props || !props.active || !props.payload || !props.payload.length) return null;
              const p = props.payload[0].payload;
              return (
                <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', padding: 8, borderRadius: 8, fontSize: 12 }}>
                  <div>DTE: {p?.dte ?? '—'}</div>
                  <div>R/R: {Math.round(p?.rr ?? 0)}</div>
                  <div>Net Δ: {Number(p?.netDelta ?? 0).toFixed(2)}</div>
                  <div>Hedge: {Math.round(p?.hedgeDisp ?? 0)}</div>
                  <div>Cost: {Math.round(p?.costUsd ?? 0)}</div>
                </div>
              );
            }} />
            {/* Neutral band |Net Δ| ≤ 0.5 shaded */}
            <ReferenceArea x1={-0.5} x2={0.5} y1={Number.NEGATIVE_INFINITY} y2={Number.POSITIVE_INFINITY} fill="#22c55e" fillOpacity={0.08} strokeOpacity={0} />
            <Legend payload={presentDteValues.map(d => ({ value: `${d} DTE`, type: 'square', color: colorForDte(d) }))} />
            <Scatter name="Combos" data={points} shape={(props) => {
              const { cx, cy, size, payload } = props;
              const r = Math.sqrt(size) / 2.5;
              const fill = colorForDte(payload.dte);
              return <circle cx={cx} cy={cy} r={Math.max(3, r)} fill={fill} stroke={fill} strokeWidth={0.5} />;
            }} onClick={(e)=> onSelectCombination?.(e?.payload?._row)} />
          </ScatterChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default OverviewPanel;


